import {
  getActiveRegion,
  getActiveTenant,
  getDomainName,
  getInfraDetails,
} from 'api-client/helpers'
import store from 'app/store'
import { sessionStoreKey } from 'core/session/sessionReducers'
import { getInfraRegionURL } from '../secrets/helper'

export interface SshKeyObject {
  name: string
  public_key: string
}

export interface NodePoolAutoscaling {
  max: number
}

export interface NodePool {
  id?: number
  flavor?: string
  replicas?: number
  network?: string
  subnet?: string
  bootingSource?: string
  volumeSize?: number
  autoscaling?: NodePoolAutoscaling
}

/**
 * Returns the cluster endpoint for the given cluster name with current domain and tenant
 * @param param0 cluster name
 * @returns cluster endpoint
 */
export const getClusterEndpoint = ({ clusterName }) => {
  const hostname = new URL(getInfraRegionURL()).host
  const domain = getDomainName()
  const tenant = getActiveTenant()
  return String(`${clusterName}.${domain}-${tenant}.k8s.${hostname}`).toLowerCase()
}

export const isPrivateRegistryEnabled = () => {
  const state = store.getState()
  const { features } = state[sessionStoreKey]
  return features?.kaapi?.enable_private_registry !== false
}

interface kaapiClusterClassParams {
  name: string
  namespace: string
  isBYOCluster?: boolean
  gpuAddons?: boolean
  network?: string
  subnet?: string
  openstackCluster?: string
  nodePools?: NodePool[]
  volumeType?: string
  volumeSize?: number
  sourceType?: string
  image?: string
  region?: string
  domain?: string
  tenant?: string
  kubernetesVersion?: string
  apiServerFlags?: Array<string>
  schedulerFlags?: Array<string>
  controllerManagerFlags?: Array<string>
  replicas?: number
  sshKey?: SshKeyObject
  controlPlaneType?: string
  masterNodePool?: NodePool[]
  proxyValue?: string
  vipInterfaceName?: string
  vip?: string
  masterReplicas?: number
}

interface MachineDeploymentParams {
  pool: NodePool
  index: number
  region: string
  openstackCluster: string
  image: string
  sshKey?: SshKeyObject
  isControlPlane?: boolean
}

const buildSshKeyVariables = (sshKey?: SshKeyObject, isControlPlane: boolean = false) =>
  sshKey
    ? !isControlPlane
      ? { sshKeyName: sshKey.name, sshAuthorizedKey: sshKey.public_key }
      : {
          keyName: sshKey.name,
          authorizedKey: sshKey.public_key,
        }
    : {}

const buildMachineDeployment = ({
  pool,
  index,
  region,
  openstackCluster,
  image,
  sshKey,
  isControlPlane,
}: MachineDeploymentParams) => ({
  class: 'ost-md',
  name: `md-${index}`,
  metadata: {
    labels: {
      'pcd-kaapi.pf9.io/region': region,
    },
    ...(pool.autoscaling
      ? {
          annotations: {
            'cluster.x-k8s.io/cluster-api-autoscaler-node-group-min-size': String(pool.replicas),
            'cluster.x-k8s.io/cluster-api-autoscaler-node-group-max-size': String(
              pool.autoscaling.max,
            ),
          },
        }
      : {}),
  },
  ...(!pool.autoscaling && { replicas: pool.replicas || 1 }),
  failureDomain: openstackCluster,
  variables: {
    overrides: [
      {
        name: 'workerNode',
        value: {
          flavor: pool.flavor,
          imageName: image,
          ...(!isControlPlane ? buildSshKeyVariables(sshKey) : {}),
          ...(pool.bootingSource && pool.bootingSource !== 'image' && isControlPlane
            ? {
                rootVolume: {
                  sizeGiB: pool.volumeSize,
                  type: pool.bootingSource,
                },
              }
            : {}),
        },
      },
      ...(sshKey && isControlPlane
        ? [
            {
              name: 'sshConfig',
              value: buildSshKeyVariables(sshKey, isControlPlane),
            },
          ]
        : []),
      {
        name: 'network',
        value: {
          networkID: pool.network,
          subnetID: pool.subnet,
        },
      },
      ...(pool.bootingSource && pool.bootingSource !== 'image' && !isControlPlane
        ? [
            {
              name: 'rootVolume',
              value: {
                sizeGiB: pool.volumeSize,
                type: pool.bootingSource,
              },
            },
          ]
        : []),
    ],
  },
})

export const createKaapiClusterClassBody = ({
  name,
  namespace,
  isBYOCluster = false,
  gpuAddons = false,
  network,
  subnet,
  openstackCluster,
  nodePools,
  image,
  region,
  domain,
  tenant,
  kubernetesVersion,
  apiServerFlags,
  schedulerFlags,
  controllerManagerFlags,
  sshKey,
  replicas = 1,
  controlPlaneType,
  masterNodePool = [],
  proxyValue,
  vipInterfaceName,
  vip,
  masterReplicas,
}: kaapiClusterClassParams) => {
  const { infraDomain, infraName } = getInfraDetails()
  // Get cas-capi-version from the version
  const casCapiVersion = `v${kubernetesVersion.split('.').slice(0, 2).join('.')}`

  return {
    apiVersion: 'cluster.x-k8s.io/v1beta1',
    kind: 'Cluster',
    metadata: {
      name,
      namespace,
      labels: {
        'cas-capi-version': casCapiVersion,
        ...(gpuAddons ? { 'gpu-addons': 'enabled' } : {}),
        ...(isPrivateRegistryEnabled() ? { 'private-registry': 'enabled' } : {}),
      },
    },
    spec: {
      topology: {
        version: kubernetesVersion,
        // Not required for HCP
        ...(controlPlaneType === 'colocated'
          ? {
              controlPlane: {
                replicas: masterNodePool?.[0]?.replicas ?? masterReplicas ?? 1,
                ...(!isBYOCluster
                  ? {
                      variables: {
                        overrides: [
                          {
                            name: 'network',
                            value: {
                              networkID: masterNodePool?.[0]?.network,
                              subnetID: masterNodePool?.[0]?.subnet,
                            },
                          },
                          ...(sshKey
                            ? [
                                {
                                  name: 'sshConfig',
                                  value: buildSshKeyVariables(
                                    sshKey,
                                    controlPlaneType === 'colocated',
                                  ),
                                },
                              ]
                            : []),
                        ],
                      },
                    }
                  : {}),
              },
            }
          : {}),
        workers: {
          machineDeployments: isBYOCluster
            ? [
                {
                  name: 'md-0',
                  class: 'byoh-md',
                  replicas: replicas,
                },
              ]
            : nodePools.map((pool, index) =>
                buildMachineDeployment({
                  pool,
                  index,
                  region,
                  openstackCluster,
                  image,
                  sshKey,
                  isControlPlane: controlPlaneType === 'colocated',
                }),
              ),
        },
        variables: [
          {
            name: 'controlPlane',
            value: {
              type: controlPlaneType,
              ...(controlPlaneType === 'colocated'
                ? {
                    proxy: { httpsProxy: proxyValue },
                    apiServerExtraArgs: apiServerFlags,
                    controllerManagerExtraArgs: controllerManagerFlags,
                    schedulerExtraArgs: schedulerFlags,
                    ...(!isBYOCluster
                      ? {
                          availabilityZones: [openstackCluster],
                          flavor: masterNodePool?.[0]?.flavor,
                          imageName: image,
                          ...(masterNodePool?.[0]?.bootingSource &&
                          masterNodePool?.[0]?.bootingSource !== 'image'
                            ? {
                                rootVolume: {
                                  sizeGiB: masterNodePool?.[0]?.volumeSize,
                                  type: masterNodePool?.[0]?.bootingSource,
                                },
                              }
                            : {}),
                        }
                      : { kubeVIPConfig: { vip: vip, networkInterface: vipInterfaceName } }),
                  }
                : {}),
            },
          },
          {
            name: 'deploymentUnit',
            value: {
              name: infraName,
              domain: infraDomain,
              region: region,
              keystoneDomain: domain,
              keystoneTenant: tenant,
            },
          },
          ...(controlPlaneType === 'colocated' && !isBYOCluster
            ? [
                {
                  name: 'sshConfig',
                  value: {
                    ...buildSshKeyVariables(sshKey, true),
                  },
                },
              ]
            : []),
          ...(!isBYOCluster
            ? [
                {
                  name: 'workerNode',
                  value: {
                    // This gets overridden by the machine deployment workerNode flavor though is mandatory to add in variables as well.
                    flavor: nodePools?.[0]?.flavor,
                    imageName: image,
                    ...(nodePools?.[0]?.bootingSource &&
                    nodePools?.[0]?.bootingSource !== 'image' &&
                    controlPlaneType === 'colocated'
                      ? {
                          rootVolume: {
                            sizeGiB: nodePools?.[0]?.volumeSize,
                            type: nodePools?.[0]?.bootingSource,
                          },
                        }
                      : {}),
                  },
                },
              ]
            : []),
          isBYOCluster
            ? {
                name: 'infrastructure',
                value: { type: 'byoh' },
              }
            : {
                name: 'network',
                value: {
                  networkID: network,
                  subnetID: subnet,
                },
              },
          // Optional: Customize kube-apiserver flags
          ...(controlPlaneType !== 'colocated'
            ? [
                {
                  name: 'apiServerFlags',
                  value: apiServerFlags,
                },
                // Optional: Customize kube-controller-manager flags
                {
                  name: 'controllerManagerFlags',
                  value: controllerManagerFlags,
                },
                // Optional: Customize kube-scheduler flags
                {
                  name: 'schedulerFlags',
                  value: schedulerFlags,
                },
              ]
            : []),
        ],
      },
    },
  }
}

interface NodeGroupTemplateParams {
  name: string
  openstackCluster: string
  nodePool: NodePool
  volumeType?: string
  volumeSize?: number
  sourceType: string
  image: string
  sshKey?: SshKeyObject
  isControlPlane?: boolean
}

export const createNodeGroupTemplateBody = ({
  name,
  openstackCluster,
  nodePool,
  volumeType,
  volumeSize,
  sourceType,
  image,
  sshKey,
  isControlPlane = false,
}: NodeGroupTemplateParams) => [
  {
    op: 'add',
    path: '/spec/topology/workers/machineDeployments/-',
    value: {
      name: name,
      class: 'ost-md',
      metadata: {
        labels: {
          'pcd-kaapi.pf9.io/region': getActiveRegion(),
        },
        ...(nodePool.autoscaling
          ? {
              annotations: {
                'cluster.x-k8s.io/cluster-api-autoscaler-node-group-min-size': String(
                  nodePool.replicas,
                ),
                'cluster.x-k8s.io/cluster-api-autoscaler-node-group-max-size': String(
                  nodePool.autoscaling.max,
                ),
              },
            }
          : {}),
      },
      ...(!nodePool.autoscaling && { replicas: nodePool.replicas || 1 }),
      failureDomain: openstackCluster,
      variables: {
        overrides: [
          {
            name: 'workerNode',
            value: {
              flavor: nodePool.flavor,
              imageName: image,
              ...(!isControlPlane ? buildSshKeyVariables(sshKey) : {}),
            },
          },
          ...(sshKey && isControlPlane
            ? [
                {
                  name: 'sshConfig',
                  value: buildSshKeyVariables(sshKey, isControlPlane),
                },
              ]
            : []),
          {
            name: 'network',
            value: {
              networkID: nodePool.network,
              subnetID: nodePool.subnet,
            },
          },
          ...(sourceType === 'volume'
            ? [
                {
                  name: 'rootVolume',
                  value: {
                    sizeGiB: volumeSize,
                    type: volumeType,
                  },
                },
              ]
            : []),
        ],
      },
    },
  },
]
